// src/components/RebalanceAlerts.js
import React, { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { useCurrency } from '../../contexts/CurrencyContext';
import { doc, setDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase/config';
import { toast } from 'react-hot-toast';
import { updateStockPrices } from '../../utils/stockPriceUpdater';

const formatPercentage = (value) => {
  if (!value && value !== 0) return '0.00%';
  return `${value.toFixed(1)}%`;
};

const getStatusColor = (difference) => {
  const absValue = Math.abs(difference);
  if (absValue >= 10) {
    return difference > 0
      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' 
      : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
  }
  return difference > 0
    ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
    : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
};

const AlertItem = ({ data, level = 0, onSelect }) => {
  const [isExpanded, setIsExpanded] = useState(level === 0);
  const hasChildren = (data.subCategories?.length > 0) || (data.assets?.length > 0);
  const isAsset = 'symbol' in data;
  const { formatAmount, convertAmount, selectedCurrency } = useCurrency();

  const getValue = (value, currency) => {
    if (!value && value !== 0) return 0;
    return convertAmount(value, currency || 'USD', selectedCurrency);
  };

  const currentValue = getValue(data.value, data.currency);
  const targetValue = getValue(data.targetValue, data.currency);

  const renderQuantity = () => {
    if (!data.quantity && data.quantity !== 0) return null;

    if (data.isCurrency) {
      const convertedQuantity = convertAmount(data.quantity, data.currency, selectedCurrency);
      return formatAmount(convertedQuantity, selectedCurrency);
    }

    // Güncel fiyat ve ortalama maliyet bazlı hesaplamalar
    if (data.lastPrice) {
      return (
        <>
          <div>{data.quantity || 0} adet</div>
          {data.quantity !== data.quantity_by_avg && (
            <div className="text-[10px] text-gray-400">
              (Ort. Maliyet: {data.quantity_by_avg || 0} adet)
            </div>
          )}
        </>
      );
    }
    
    return `${data.quantity} adet`;
  };

  const renderContent = () => (
    <div 
      className={`flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer rounded-lg ${
        level === 0 ? 'bg-gray-50 dark:bg-gray-800/50' : 
        level === 1 ? 'bg-gray-100/50 dark:bg-gray-800/30' : 
        'bg-white dark:bg-[#151934]'
      }`}
      onClick={() => {
        if (hasChildren) {
          setIsExpanded(!isExpanded);
        } else if (isAsset) {
          setIsExpanded(!isExpanded);
        }
        onSelect?.(data);
      }}
    >
      <div className="flex items-center gap-3">
        {hasChildren && (
          <div className="w-4 h-4 text-gray-500 dark:text-gray-400">
            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </div>
        )}
        <div>
          <div className="font-medium text-gray-900 dark:text-gray-100">
            {isAsset ? data.symbol : data.category}
            {data.name && !isAsset && (
              <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                ({data.name})
              </span>
            )}
          </div>
          
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Mevcut: {formatPercentage(data.currentAllocation)} | Hedef: {formatPercentage(data.targetAllocation)}
          </div>
          
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Mevcut: {formatAmount(currentValue)} | Hedef: {formatAmount(targetValue)}
          </div>

          {isAsset && (
            <>
              <div className="text-xs mt-1 flex gap-3">
                {/* RSI Badge */}
                <div className={`inline-flex items-center justify-center px-2.5 py-1.5 rounded-full ${
                  data.rsi1h >= 70 ? 'bg-red-100 dark:bg-red-900/30' : 
                  data.rsi1h <= 30 ? 'bg-green-100 dark:bg-green-900/30' : 
                  'bg-yellow-100 dark:bg-yellow-900/30'
                }`}>
                  <span className={`font-medium ${
                    data.rsi1h >= 70 ? 'text-red-700 dark:text-red-400' : 
                    data.rsi1h <= 30 ? 'text-green-700 dark:text-green-400' : 
                    'text-yellow-700 dark:text-yellow-400'
                  }`}>
                    RSI: {data.rsi1h?.toFixed(0) || '-'}
                    {data.rsi1h >= 70 ? ' ↑' : data.rsi1h <= 30 ? ' ↓' : ' →'}
                  </span>
                </div>

                {/* EMA Badge */}
                <div className={`inline-flex items-center justify-center w-28 px-2.5 py-1.5 rounded ${
                  data.ema7_1h > data.ema21_1h ? 'bg-green-100 dark:bg-green-900/30' :
                  data.ema7_1h < data.ema21_1h ? 'bg-red-100 dark:bg-red-900/30' :
                  'bg-yellow-100 dark:bg-yellow-900/30'
                }`}>
                  <span className={`font-medium ${
                    data.ema7_1h > data.ema21_1h ? 'text-green-700 dark:text-green-400' :
                    data.ema7_1h < data.ema21_1h ? 'text-red-700 dark:text-red-400' :
                    'text-yellow-700 dark:text-yellow-400'
                  }`}>
                    EMA: {data.ema21_1h?.toFixed(1) || '-'}
                    {data.ema7_1h > data.ema21_1h ? ' ↑' : data.ema7_1h < data.ema21_1h ? ' ↓' : ' →'}
                  </span>
                </div>

                {/* Price Badge */}
                {data.lastPrice && (
                  <div className="inline-flex items-center justify-center w-28 px-2.5 py-1.5 rounded bg-blue-50 dark:bg-blue-900/20">
                    <span className="font-medium text-blue-700 dark:text-blue-400">
                      {formatAmount(data.lastPrice)}
                    </span>
                  </div>
                )}
              </div>

              {/* Detaylı Göstergeler */}
              {isExpanded && (
                <div className="text-xs mt-1 grid grid-cols-4 gap-2 text-gray-500 dark:text-gray-400">
                  <div>EMA7: {data.ema7_1h?.toFixed(2) || '-'}</div>
                  <div>EMA21: {data.ema21_1h?.toFixed(2) || '-'}</div>
                  <div>EMA55: {data.ema55_1h?.toFixed(2) || '-'}</div>
                  <div>EMA100: {data.ema100_1h?.toFixed(2) || '-'}</div>
                </div>
              )}
            </>
          )}

          {data.isCurrency && (
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Orijinal: {formatAmount(data.position || data.quantity || 0, data.currency)}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col items-end">
        <div 
          className={`px-3 py-1 rounded-full text-sm font-medium ${
            getStatusColor(data.difference)
          }`}
        >
          {data.difference > 0 ? 'Al' : 'Sat'} {formatPercentage(Math.abs(data.difference))}
        </div>

        {(isAsset || data.isCurrency) && data.quantity && (
          <>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {renderQuantity()} {data.difference > 0 ? 'alınmalı' : 'satılmalı'}
            </div>
            {data.lastPrice && (
              <div className="text-xs text-gray-400">
                {formatAmount(data.lastPrice)} birim fiyatla
              </div>
            )}
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Tahmini: {formatAmount(getValue(data.estimatedCost, data.currency))}
            </div>
          </>
        )}
      </div>
    </div>
  );

  const renderChildren = () => {
    if (!isExpanded) return null;

    return (
      <>
        {data.subCategories?.map((sub, index) => (
          <AlertItem 
            key={`${sub.category}-${index}`} 
            data={sub}
            level={level + 1} 
            onSelect={onSelect}
          />
        ))}
        {data.assets?.map((asset, index) => (
          <AlertItem 
            key={`${asset.symbol}-${index}`} 
            data={asset}
            level={level + 1} 
            onSelect={onSelect}
          />
        ))}
      </>
    );
  };

  return (
    <div className={`mb-2 ${level > 0 ? 'ml-6' : ''}`}>
      {renderContent()}
      {renderChildren()}
    </div>
  );
};

const RebalanceAlerts = ({ alerts, onSelectCategory, userEmail, portfolioId }) => {
  const [isInitializing, setIsInitializing] = useState(false);

  if (!alerts || alerts.length === 0) return null;

  const handleInitializePositions = async () => {
    if (!userEmail || isInitializing) return;

    const toastId = toast.loading('Eksik hisseler ekleniyor...');
    setIsInitializing(true);

    try {
      if (!portfolioId) {
        toast.error('Lütfen bir portföy seçin');
        return;
      }

      // Mevcut position'ları al
      const portfolioDoc = doc(db, 'users', userEmail, 'portfolios', portfolioId);
      const positionsRef = collection(portfolioDoc, 'positions');
      const positionsSnapshot = await getDocs(positionsRef);
      const existingSymbols = new Set(positionsSnapshot.docs.map(doc => doc.id));

      // Alarm vermiş tüm sembolleri topla
      const missingSymbols = new Set();
      alerts.forEach(alert => {
        // Alt kategorilerdeki varlıkları kontrol et
        alert.subCategories?.forEach(sub => {
          sub.assets?.forEach(asset => {
            if (!existingSymbols.has(asset.symbol)) {
              missingSymbols.add(asset.symbol);
            }
          });
        });
        // Ana kategorideki varlıkları kontrol et
        alert.assets?.forEach(asset => {
          if (!existingSymbols.has(asset.symbol)) {
            missingSymbols.add(asset.symbol);
          }
        });
      });

      // Eksik position'ları oluştur
      for (const symbol of missingSymbols) {
        const positionRef = doc(portfolioDoc, 'positions', symbol);
        await setDoc(positionRef, {
          symbol,
          quantity: 0,
          value: 0,
          lastPrice: 0,
          avgCost: 0,
          rsi1h: null,
          ema7_1h: null,
          ema21_1h: null,
          ema55_1h: null,
          ema100_1h: null,
          currency: 'USD',
          exchange: 'ARCA',
          secType: 'STK',
          createdAt: new Date(),
          updatedAt: new Date(),
          lastUpdated: new Date()
        });
      }

      // Fiyatları güncelle
      if (missingSymbols.size > 0) {
        await updateStockPrices(userEmail, portfolioId);
        toast.success(`${missingSymbols.size} adet eksik hisse eklendi ve fiyatlar güncellendi`, {
          id: toastId
        });
      } else {
        toast.success('Tüm hisseler zaten mevcut', { id: toastId });
      }
    } catch (error) {
      console.error('Position oluşturma hatası:', error);
      toast.error('Hisse eklenirken bir hata oluştu', { id: toastId });
    } finally {
      setIsInitializing(false);
    }
  };

  return (
    <div className="bg-white dark:bg-[#151934] rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-100 dark:border-yellow-900/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500 dark:text-yellow-400" />
            <h3 className="font-medium text-yellow-800 dark:text-yellow-200">
              Rebalancing Önerileri ({alerts.length})
            </h3>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-sm text-yellow-600 dark:text-yellow-300">
              {alerts.filter(a => a.difference > 0).length} Al |{' '}
              {alerts.filter(a => a.difference < 0).length} Sat
            </div>

            <button
              onClick={handleInitializePositions}
              disabled={isInitializing}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              {isInitializing ? 'Ekleniyor...' : 'Update TA'}
            </button>
          </div>
        </div>
        
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          Öneriler öncelik sırasına göre sıralanmıştır. Her kategori için en öncelikli işlemden başlayınız.
        </p>
      </div>

      <div className="p-4 divide-y divide-gray-100 dark:divide-gray-800">
        {alerts.map((alert, index) => (
          <AlertItem 
            key={`${alert.category}-${index}`} 
            data={alert} 
            level={0} 
            onSelect={onSelectCategory}
          />
        ))}
      </div>
    </div>
  );
};

export default RebalanceAlerts;